import { Aperture, Camera, Lightbulb, type LucideIcon, Mic, Package, Video } from 'lucide-react'

import { brandTone } from '@/lib/brand-tones'

/**
 * A glyph per equipment category.
 *
 * Keyed by category SLUG rather than by an id, because categories are editorial
 * — the panel can add "gimbale" without a migration — and an unknown slug has
 * to keep rendering something where an icon belongs.
 *
 * It lives in `lib/` rather than inside a component because three surfaces read
 * it: the filter pills, the card, and the detail page's breadcrumb. Put it in
 * any one of them and the other two have to import a component to get at a map.
 */
const CATEGORY_ICON: Record<string, LucideIcon> = {
  aparat: Camera,
  obiektyw: Aperture,
  kamera: Video,
  swiatlo: Lightbulb,
  dzwiek: Mic,
  statyw: Package,
}

/** The generic box is the fallback, never nothing. */
export function iconFor(slug: string): LucideIcon {
  return CATEGORY_ICON[slug] ?? Package
}

/**
 * A hue per equipment category, from the panel's accent ramp.
 *
 * The grid is eleven near-identical dark cards and the one thing that tells
 * them apart at a glance is what KIND of thing each is — which was printed in
 * the same grey on every one of them. A category you can recognise by colour
 * before you have read the word is the difference between scanning a list and
 * reading it.
 *
 * Six stops of the BRAND palette, by position rather than by colour. Three of
 * the literals this replaces — an amber, a teal and a green — were off the
 * arc, and the owner's rule is the whole site: decorative colour is the FLOU
 * gradient or it is nothing. See `lib/brand-tones.ts`.
 *
 * Used on the card's badge and nowhere near the photograph itself — anything
 * saturated under a cut-out fights it.
 */
const CATEGORY_STOP: Record<string, number> = {
  aparat: 0,
  obiektyw: 3,
  kamera: 6,
  swiatlo: 1,
  dzwiek: 4,
  statyw: 7,
}

/** The first stop stands in for a category nobody has themed yet. */
export function toneFor(slug: string): string {
  return brandTone(CATEGORY_STOP[slug] ?? 0)
}
